import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { PERMISSION_MODAL_TYPE, PERMISSION_TYPE, SCREEN, SCROLL_TARGET_TYPE, TRANSITION_SOURCE_PAGE_STATE } from '../../../Common';
import { BreadcrumbItem } from '../../molecules/BreadcrumbsList';
import BreadcrumbHeader from '../../organisms/BreadcrumbHeader';
import PermissionModal from '../../organisms/PermissionModal';
import { AppState } from '../../../redux/modules/store';
import { FetchCustomerInfoAllKey, clearCustomerInfoAll, updateRefresh } from '../../../redux/operations/CustomerInfoAll';

import { isCrewModule } from '../../../utils/Env';
import { getBreadcrumbInfoAtFirstLevel } from '../../../utils/LocationHistoryUtils';
import SummaryPreviewContainer from '../../organisms/SummaryPreviewContainer';

/**
 * スクロールターゲット
 */
export type scrollTargetType = typeof SCROLL_TARGET_TYPE[keyof typeof SCROLL_TARGET_TYPE];
/**
 * サマリー画面のパラメータ定義
 */
export interface SummaryPreviewParams {
  customerId: string;
  scrollTargetType?: scrollTargetType;
  id?: string;
}

export interface ScrollTargetInfo {
  scrollTargetType: scrollTargetType;
  id: string;
}
export interface SummaryPreviewCommonProps {
  defaultViewportMetaContent: string;
  userScalableViewportMetaContent: string;
  /**
   * 顧客ID
   */
  customerId: string;
  /**
   * 初期スクロール情報
   */
  scrollTargetInfo?: ScrollTargetInfo;
  getConfidentialPermission?: () => Promise<string | null>;
  postConfidentialPermission?: (permissionType: string) => void;
  previewSummaryClassName?: string;
  sourceNegotiationName?: string;
  sourceNegotiationId?: string;
}

const SummaryPreviewCommon: React.FC<SummaryPreviewCommonProps> = ({
  defaultViewportMetaContent,
  userScalableViewportMetaContent,
  customerId,
  scrollTargetInfo,
  getConfidentialPermission,
  postConfidentialPermission,
  previewSummaryClassName = '',
  sourceNegotiationName,
  sourceNegotiationId,
}) => {
  // custom Hooks
  const dispatch = useDispatch();
  const history = useHistory();

  // global State
  const fetchLoadingStatus = useSelector((state: AppState) => state.loadingStatus.fetchCustomerInfoAll);
  const {
    customerId: stateCustomerId,
    customerNumber: stateCustomerNumber,
    ownStoreFlg,
  } = useSelector((state: AppState) => state.customerInfoAll);
  const locationHistory = useSelector((state: AppState) => state.locationHistory);

  // local State
  const [isOpenPermissionModal, setIsOpenPermissionModal] = useState(false);
  const [permittedFlag, setPermittedFlag] = useState(false);
  const [actType, setActType] = useState('');
  const [targetNegotiationId, setTargetNegotiationId] = useState('');
  const [targetProductId, setTargetProductId] = useState('');
  const [targetActionId, setTargetActionId] = useState('');

  // local State
  // 前回取得した値を参照しないようにするフラグ ※前回開いた情報が一瞬画面に表示されてしまう現象の対策
  const [isBeforeInitialize, setIsBeforeInitialize] = useState(true);
  // お客さま情報が存在しないフラグ
  const isDataNotFound = stateCustomerId === 0 && stateCustomerNumber === '';

  // クルーモジュールであるかどうかにより、ぱんくずリストの表示内容を変更
  // クルーモジュールの場合
  //   お客さま情報が取得できたら    => お客さま情報画面から遷移したと判定し、パンくずには商談一覧、お客さま情報を表示
  //   お客さま情報が取得できない場合 => パンくずには商談一覧のみを表示
  //
  // クルーモジュール以外の場合 => 商談一覧画面から遷移したと判定し、パンくずには商談一覧のみを表示
  const firstLevelBreadcrumb = getBreadcrumbInfoAtFirstLevel(locationHistory.pathNames);
  // 商談詳細から遷移してきた場合のみ、商談名パンくずを追加する
  const negotiationBreadcrumb: BreadcrumbItem[] = sourceNegotiationName
    ? [
        {
          text: sourceNegotiationName,
          to: `${SCREEN.NEGOTIATION_DETAIL.URL}/${sourceNegotiationId}`,
        },
      ]
    : [];
  const breadcrumbsList: BreadcrumbItem[] =
    isCrewModule() && !isDataNotFound && ownStoreFlg === '1'
      ? [
          firstLevelBreadcrumb,
          {
            text: 'お客さま情報',
            to: `${SCREEN.CUSTOMER_DETAIL.URL}/${customerId}`,
          },
          ...negotiationBreadcrumb,
          {
            text: SCREEN.SUMMARY_PREVIEW.NAME,
          },
        ]
      : [
          firstLevelBreadcrumb,
          ...negotiationBreadcrumb,
          {
            text: SCREEN.SUMMARY_PREVIEW.NAME,
          },
        ];

  // 編集ボタン押下時のイベントハンドラー
  const onClickCustomerEdit = () => {
    // お客さま情報編集画面に遷移します
    history.push(`${SCREEN.CUSTOMER_CREATE.URL}/${customerId}`, TRANSITION_SOURCE_PAGE_STATE.SUMMARY);
  };

  const onClickNegoEdit = (negotiationId: string) => {
    // 商談情報編集画面に遷移します
    history.push(`${SCREEN.NEGOTIATION_CREATE.URL}/${customerId}/${negotiationId}`, TRANSITION_SOURCE_PAGE_STATE.SUMMARY);
  };

  const onClickProductEdit = (negotiationId: string, productId: string) => {
    // 商品情報編集画面に遷移します
    history.push({
      pathname: `${SCREEN.PRODUCT_CREATE.URL}/${negotiationId}/${productId}`,
      state: {
        customerId: customerId,
      },
    });
  };

  const onClickActionEdit = (negotiationId: string, actionId: string) => {
    // アクション情報編集画面に遷移します
    history.push({
      pathname: `${SCREEN.ACTION_CREATE.URL}/${negotiationId}/${actionId}`,
      state: {
        customerId: customerId,
        fromSummaryPage: true,
      },
    });
  };

  const doAction = async (actType?: string, negotiationId?: string, productId?: string, actionId?: string) => {
    if (actType === 'onEditNego' && negotiationId) {
      onClickNegoEdit(negotiationId);
    }

    if (actType === 'onEditProduct' && negotiationId && productId) {
      onClickProductEdit(negotiationId, productId);
    }

    if (actType === 'onEditAction' && negotiationId && actionId) {
      onClickActionEdit(negotiationId, actionId);
    }
  };

  // 情報保持許諾の存在確認
  const checkConfidentialPermission = async () => {
    const permissionType = (getConfidentialPermission && (await getConfidentialPermission())) || '';
    if (permissionType) {
      setPermittedFlag(true);
      return true;
    } else {
      setPermittedFlag(false);
      return false;
    }
  };

  // パーミッションモーダル表示
  const openPermissionModal = async (actType: string, negotiationId?: string, productId?: string, actionId?: string) => {
    setActType(actType);
    setTargetNegotiationId(negotiationId || '');
    setTargetProductId(productId || '');
    setTargetActionId(actionId || '');

    if (actType === 'onEditCustomer') {
      onClickCustomerEdit();
    } else {
      const isPermitted = await checkConfidentialPermission();
      if (isPermitted) {
        doAction(actType, negotiationId, productId, actionId);
      } else {
        setIsOpenPermissionModal(true);
      }
    }
  };

  //◆承諾の場合
  const onPermissionAccept = (permissionType: string) => {
    setIsOpenPermissionModal(false);
    if (permissionType !== PERMISSION_TYPE.EDIT_INFO) {
      postConfidentialPermission && postConfidentialPermission(permissionType);
    }
    doAction(actType, targetNegotiationId, targetProductId, targetActionId);
  };

  // 初期処理
  const init = () => {
    // 初期表示（連携遷移）時、ローディング表示
    const key: FetchCustomerInfoAllKey = {
      customerId: customerId,
    };
    updateRefresh(key, dispatch);
    setIsBeforeInitialize(false);
  };

  // cleanUp処理
  const cleanUp = () => {
    clearCustomerInfoAll(dispatch);
  };

  // useEffect hooks
  useEffect(() => {
    // 初期処理
    init();
    // cleanUp処理
    return cleanUp;
  }, []);

  // 編集可能代理店判定(ログイン代理店、およびそのデュアル店舗である場合は、編集可能、それ以外は読み取り専用)
  const isLoginAgencyCd = ownStoreFlg === '1';

  // 編集可能フラグ
  const isEditable = isCrewModule() && isLoginAgencyCd;

  return (
    <div className='layout-tab'>
      <div className='layout-tab__header u-bg-white'>
        <div className='container'>
          <BreadcrumbHeader
            breadcrumbsList={breadcrumbsList}
            screenId={SCREEN.SUMMARY_PREVIEW.ID}
            loadingStatus={fetchLoadingStatus}
            isInnerHeader={true}
            disableSystemError={true}
            data-testid='bread-crumb-header'
            data-testid-e2e='bread-crumb-header'
          />
        </div>
      </div>
      <hr className='uc-divider' />
      <SummaryPreviewContainer
        defaultViewportMetaContent={defaultViewportMetaContent}
        userScalableViewportMetaContent={userScalableViewportMetaContent}
        isBeforeInitialize={isBeforeInitialize}
        customerId={customerId}
        isEditable={isEditable}
        errorMessageType={'customer'}
        scrollTargetInfo={scrollTargetInfo}
        getConfidentialPermission={getConfidentialPermission}
        postConfidentialPermission={postConfidentialPermission}
        openPermissionModal={openPermissionModal}
        previewSummaryClassName={previewSummaryClassName}
      />
      <PermissionModal
        isOpen={isOpenPermissionModal}
        permittedFlag={permittedFlag}
        onClose={() => setIsOpenPermissionModal(false)}
        onAccept={onPermissionAccept}
        modalType={PERMISSION_MODAL_TYPE.INFORMATION_EDIT}
      />
    </div>
  );
};

export default SummaryPreviewCommon;
